import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import ProjectProgressBar from "../components/ProjectProgressBar";
import { useAuth } from "../context/AuthContext";
import { FolderKanban, FileWarning, AlertTriangle, Clock, ArrowRight, Plus, BarChart3, TrendingUp, Briefcase, Hammer } from "lucide-react";
import { motion } from "framer-motion";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [myTasks, setMyTasks] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, p, t] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/projects"),
        api.get("/dashboard/my-tasks"),
      ]);
      setStats(s.data);
      setProjects(p.data.projects || []);
      setMyTasks(t.data.tasks || []);
    })();
  }, []);

  const metrics = [
    { key: "active_projects", label: "Active projects", value: stats?.active_projects ?? "—", icon: FolderKanban, tint: "#CCFBF1", fg: "#0F766E" },
    { key: "pending_vos", label: "VOs awaiting customer", value: stats?.pending_vos ?? "—", icon: FileWarning, tint: "#FFEDD5", fg: "#C2410C" },
    { key: "my_defects", label: user?.role === "admin" ? "Open defects" : "My defects", value: stats?.my_defects ?? "—", icon: Hammer, tint: "#FCE7F3", fg: "#9D174D", href: "/projects?filter=defects" },
    { key: "overdue_tasks", label: "Open tasks", value: stats?.overdue_tasks ?? "—", icon: AlertTriangle, tint: "#FEE2E2", fg: "#9F1239" },
    { key: "stale_projects", label: "No update 5+ days", value: stats?.stale_projects ?? "—", icon: Clock, tint: "#DBEAFE", fg: "#1E40AF" },
  ];

  return (
    <div className="max-w-7xl mx-auto py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Dashboard</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#2C2A29] tracking-tight mt-2">
            Good to see you, <span className="italic text-[#E07A5F]">{user?.name?.split(" ")[0]}</span>.
          </h1>
        </div>
        <Link to="/projects/new" data-testid="new-project-btn" className="btn-primary inline-flex items-center gap-2">
          <Plus size={16} /> New project
        </Link>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 mb-10">
        {metrics.map((m, i) => (
          <motion.div
            key={m.key}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            className="card-soft"
            data-testid={`metric-${m.key}`}
          >
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-5" style={{ background: m.tint, color: m.fg }}>
              <m.icon size={20} />
            </div>
            <div className="text-3xl font-display font-bold text-[#2C2A29]">{m.value}</div>
            <div className="text-sm text-[#7A7571] mt-1">{m.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Full-width workspace / portfolio rollup — sits above the two-column
          Projects + Waiting-on-you grid so it never gets squeezed by the
          right rail. */}
      {stats?.cost_rollup && (
        <div className="mb-8">
          <CostRollupBand rollup={stats.cost_rollup} isAdmin={user?.role === "admin"} />
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Projects list */}
        <div className="lg:col-span-8 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-semibold text-[#2C2A29]">Projects</h2>
            <Link to="/projects" className="text-sm text-[#E07A5F] font-semibold hover:underline">See all</Link>
          </div>
          {projects.length === 0 && (
            <div className="card-soft text-center py-12">
              <p className="text-[#7A7571] mb-4">No projects yet.</p>
              <Link to="/projects/new" className="btn-primary inline-flex items-center gap-2"><Plus size={16} />Create your first project</Link>
            </div>
          )}
          {projects.slice(0, 6).map((p) => (
            <Link
              key={p.project_id}
              to={`/projects/${p.project_id}`}
              data-testid={`project-row-${p.project_id}`}
              className="card-soft p-5 !rounded-2xl hover:-translate-y-0.5 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-5"
            >
              <div className="min-w-0 lg:flex-1">
                <div className="font-display text-lg font-semibold text-[#2C2A29] truncate">{p.name}</div>
                <div className="text-sm text-[#7A7571] truncate">{p.address}</div>
              </div>
              <div className="w-full lg:w-52 lg:flex-shrink-0">
                <ProjectProgressBar value={p.progress_pct || 0} />
              </div>
              <div className="flex items-center justify-between lg:justify-end gap-4 lg:flex-shrink-0">
                <div className="flex flex-col lg:items-end">
                  <div className="text-xs text-[#7A7571]">Current value</div>
                  <div className="font-semibold text-[#2C2A29]">${Number(p.current_project_value || 0).toLocaleString()}</div>
                </div>
                <ArrowRight size={18} className="text-[#7A7571]" />
              </div>
            </Link>
          ))}
        </div>

        {/* My tasks */}
        <div className="lg:col-span-4">
          <div className="bg-[#F7F4EC] rounded-3xl p-6">
            <h2 className="font-display text-xl font-semibold text-[#2C2A29] mb-1">Waiting on you</h2>
            <p className="text-sm text-[#7A7571] mb-5">{myTasks.length} open {myTasks.length === 1 ? "task" : "tasks"}</p>
            <div className="space-y-3">
              {myTasks.length === 0 && <p className="text-sm text-[#7A7571]">You're all caught up ✨</p>}
              {myTasks.map((t) => (
                <div key={t.task_id} data-testid={`my-task-${t.task_id}`} className="bg-white rounded-2xl p-4 border border-[#EBE5D9]">
                  <div className="text-sm font-semibold text-[#2C2A29] mb-2">{t.title}</div>
                  <StatusBadge status={t.status} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CostRollupBand({ rollup, isAdmin }) {
  const money = (n) => {
    const v = Number(n || 0);
    return `${v < 0 ? "−" : ""}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const margin = Number(rollup.margin || 0);
  return (
    <div
      className="card-soft"
      data-testid="dashboard-cost-rollup"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">
            {isAdmin ? "Workspace-wide total" : "Your portfolio (PIC)"}
          </div>
          <div className="text-sm text-[#7A7571] mt-1">
            {isAdmin
              ? "Aggregate across every project owned by every teammate."
              : "Rolled up across projects where you are the Person In Charge."}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <RollupMetric
          icon={<Briefcase size={14} />} label="Total projects"
          value={Number(rollup.projects_count || 0).toLocaleString()}
          testid="rollup-projects"
        />
        <RollupMetric
          icon={<BarChart3 size={14} />} label="Scope"
          value={money(rollup.scope_value)}
          testid="rollup-scope"
        />
        <RollupMetric
          icon={<TrendingUp size={14} />} label="Margin"
          value={money(margin)}
          tone={margin >= 0 ? "positive" : "negative"}
          testid="rollup-margin"
        />
        <RollupMetric
          icon={<TrendingUp size={14} />} label="Revenue collected"
          value={money(rollup.revenue)}
          tone="positive"
          testid="rollup-revenue"
        />
      </div>
    </div>
  );
}

function RollupMetric({ icon, label, value, tone, testid }) {
  const cls = tone === "positive"
    ? "text-[#166534]"
    : tone === "negative"
    ? "text-[#9F1239]"
    : "text-[#2C2A29]";
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest font-bold text-[#7A7571] flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className={`font-display text-2xl font-bold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
